import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@entry/navigation";

import { Eye, EyeClosed } from "@assets";
import { TextInput } from "@components/Input";
import { Link } from "@components/Link";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Routes, StorageKey } from "@ctypes";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useLogin, useOnboardingSavePoint } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { validationSchema } from "./content";
import { LoginFormValues } from "./types";

export const LoginRoute = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { showErrorBanner } = useErrorBanner();

  const [login] = useLogin();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: localStorage.getItem(StorageKey.Email) ?? "",
      password: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [navigateToNextRoute] = useOnboardingSavePoint();
  const navigate = useNavigate();

  const emailController = register("email");
  const passwordController = register("password");

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async ({ email, password }: LoginFormValues) => {
    try {
      localStorage.setItem(StorageKey.Email, email);
      setLoading(true);

      const { data } = await login({
        variables: {
          email,
          password,
        },
      });

      const token = data?.loginUser.sessionJwt;

      if (token) {
        localStorage.setItem(StorageKey.Token, token);

        await navigateToNextRoute();
      } else {
        showErrorBanner();
      }
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.EntryLoginFormTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.LoginRoutePrimaryCTA),
        onClick: handleSubmit(handleLogin),
        disabled: !isValid,
        loading,
      },
    },
  };

  return (
    <EntryStepLayout {...props}>
      <TextInput
        {...emailController}
        label={i18n.t(Translations.LoginRouteEmailLabel)}
        value={watch("email")}
        onChangeText={(text) => setValue("email", text, constants.form)}
        error={errors.email?.message}
        placeholder={i18n.t(Translations.LoginRouteEmailPlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...passwordController}
        label={i18n.t(Translations.LoginRoutePasswordLabel)}
        value={watch("password")}
        onChangeText={(text) => setValue("password", text, constants.form)}
        type={showPassword ? "text" : "password"}
        error={errors.password?.message}
        rightIcon={showPassword ? <EyeClosed /> : <Eye />}
        onRightIconClick={toggleShowPassword}
        placeholder={i18n.t(Translations.LoginRoutePasswordPlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      <Link
        text={i18n.t(Translations.LoginRouteForgotPasswordCTA)}
        onClick={() => {
          navigate(Routes.ForgotPassword);
        }}
      />
    </EntryStepLayout>
  );
};
